import React, { useState } from 'react';
import { X, Ticket, ExternalLink, AlertCircle, MapPin } from 'lucide-react';
import { affiliateTicketUrl, hasAffiliateSetup } from '../services/affiliateLinks';

interface Props {
  eventId: string;
  eventName: string;
  venueName: string;
  ticketUrl: string;
  onClose: () => void;
}

const API_KEY = 'WZzC1rWNx5i2BfpRIMGttI9cPY06bn3b';

export default function TicketModal({ eventId, eventName, venueName, ticketUrl, onClose }: Props) {
  const [iframeError, setIframeError] = useState(false);
  const igniteUrl = `https://ignite.ticketmaster.com/v1/widget/purchase?apikey=${API_KEY}&event_id=${eventId}&theme=dark`;
  const trackedUrl = affiliateTicketUrl(ticketUrl);

  return (
    // Side panel — right edge of screen, map still visible behind
    <div className="fixed inset-y-0 right-0 z-50 flex items-stretch" style={{ width: 'min(420px, 95vw)' }}>
      {/* Transparent clickaway zone on the left */}
      <div className="absolute inset-y-0 right-full w-screen" onClick={onClose} />

      <div className="relative flex flex-col w-full bg-black/92 border-l border-cyan-400/30 shadow-[0_0_40px_rgba(0,238,255,0.15)] backdrop-blur-sm">

        {/* Neon top border glow */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />

        {/* Header */}
        <div className="flex items-start justify-between px-4 py-3 border-b border-cyan-900/40">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Ticket size={13} className="text-cyan-400 shrink-0" />
              <span className="font-orbitron text-xs font-bold text-cyan-400 tracking-widest uppercase">
                GET TICKETS
              </span>
            </div>
            <div className="text-amber-400 text-sm font-bold truncate">{eventName}</div>
            <div className="text-cyan-800 text-xs flex items-center gap-1 mt-0.5">
              <MapPin size={9} />{venueName}
            </div>
          </div>
          <button onClick={onClose} className="ml-3 text-cyan-900 hover:text-cyan-400 transition-colors shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* "You are here" immersion text */}
        <div className="px-4 py-2 border-b border-cyan-950/60 bg-cyan-950/20">
          <div className="text-cyan-600 text-xs font-mono tracking-wider animate-pulse">
            ◈ LOCATION LOCKED — YOU ARE AT THE VENUE
          </div>
        </div>

        {/* Ticket embed */}
        <div className="flex-1 overflow-hidden">
          {!iframeError ? (
            <iframe
              src={igniteUrl}
              className="w-full h-full border-0"
              onError={() => setIframeError(true)}
              allow="payment"
              title="Buy Tickets"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-5 p-8 h-full text-center">
              <div className="w-16 h-16 rounded-full border-2 border-cyan-500/40 flex items-center justify-center">
                <Ticket size={28} className="text-cyan-500" />
              </div>
              <div className="space-y-1">
                <div className="text-amber-400 font-bold font-mono text-sm">{eventName}</div>
                <div className="text-cyan-700 text-xs">{venueName}</div>
              </div>
              <a
                href={trackedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-cyan-500/20 border border-cyan-400/60 text-cyan-300 font-bold font-mono text-sm uppercase tracking-widest rounded-sm hover:bg-cyan-500/30 hover:border-cyan-300 transition-all"
                style={{ boxShadow: '0 0 20px rgba(0,238,255,0.2)' }}
              >
                <ExternalLink size={14} /> BUY ON TICKETMASTER
              </a>
              {hasAffiliateSetup() && (
                <div className="text-cyan-900 text-xs font-mono">✓ Affiliate tracking active</div>
              )}
              <div className="text-cyan-900 text-xs font-mono">
                Opens official Ticketmaster purchase page
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-cyan-950/60 flex items-center justify-between">
          <span className="text-cyan-950 text-xs font-mono">Powered by Ticketmaster Ignite</span>
          <a href={trackedUrl} target="_blank" rel="noopener noreferrer"
            className="text-cyan-800 hover:text-cyan-500 text-xs font-mono flex items-center gap-1 transition-colors">
            full page <ExternalLink size={9} />
          </a>
        </div>
      </div>
    </div>
  );
}
